// captions/vtt.ts — caption files (1.27.60). The uploader brings .srt or .vtt; everything is
// WebVTT from here on (the server only accepts WebVTT). Cues are shown as PLAIN TEXT — cue
// markup like <i>, <b>, <c.x> and <v Speaker> is stripped, so a caption file can never inject
// anything into the page.

export interface Cue {
  /** Seconds. */
  start: number;
  end: number;
  text: string;
}

const TIMING = /^((?:\d{1,2}:)?\d{2}:\d{2}[.,]\d{3})\s+-->\s+((?:\d{1,2}:)?\d{2}:\d{2}[.,]\d{3})/;

function toSeconds(stamp: string): number {
  const [clock, ms = '0'] = stamp.replace(',', '.').split('.');
  const parts = clock!.split(':').map(Number);
  const [h, m, s] = parts.length === 3 ? parts : [0, parts[0]!, parts[1]!];
  return h! * 3600 + m! * 60 + s! + Number(ms) / 1000;
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&nbsp;': ' ',
  '&lrm;': '',
  '&rlm;': '',
  '&quot;': '"',
  '&#39;': "'",
};

/** A cue's text as plain text: markup stripped, entities decoded, lines kept. */
export function plainCueText(raw: string): string {
  return raw
    .replace(/<[^>]*>/g, '')
    .replace(/&(amp|lt|gt|nbsp|lrm|rlm|quot|#39);/g, (m) => ENTITIES[m] ?? m)
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n');
}

/** Cues in time order. Anything that is not a cue (NOTE, STYLE, REGION blocks) is skipped. */
export function parseVtt(text: string): Cue[] {
  const blocks = text
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/);
  const cues: Cue[] = [];
  for (const block of blocks) {
    const lines = block.split('\n');
    const at = lines.findIndex((l) => TIMING.test(l.trim()));
    if (at < 0) continue;
    const m = TIMING.exec(lines[at]!.trim())!;
    const start = toSeconds(m[1]!);
    const end = toSeconds(m[2]!);
    const body = plainCueText(lines.slice(at + 1).join('\n'));
    if (end > start && body) cues.push({ start, end, text: body });
  }
  return cues.sort((a, b) => a.start - b.start);
}

/** SubRip → WebVTT: comma decimals become dots, cue numbers are dropped, header added. */
export function srtToVtt(srt: string): string {
  const cues = parseVtt(srt); // TIMING accepts both "," and "."
  return cuesToVtt(cues);
}

export function formatVttTime(sec: number): string {
  const total = Math.max(0, Math.round(sec * 1000));
  const ms = total % 1000;
  const s = Math.floor(total / 1000) % 60;
  const m = Math.floor(total / 60000) % 60;
  const h = Math.floor(total / 3600000);
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(ms, 3)}`;
}

/** Cues back to a WebVTT file (after the uploader corrects automatic captions). */
export function cuesToVtt(cues: Cue[]): string {
  const body = cues
    .filter((c) => c.end > c.start && c.text.trim())
    .map((c) => `${formatVttTime(c.start)} --> ${formatVttTime(c.end)}\n${c.text.trim()}`)
    .join('\n\n');
  return `WEBVTT\n\n${body}\n`;
}

/** The cue showing at `t` (the latest one that started, if cues overlap), or null. */
export function cueAt(cues: Cue[], t: number): Cue | null {
  let lo = 0;
  let hi = cues.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid]!.start <= t) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  for (let i = found; i >= 0 && i > found - 4; i--) {
    if (cues[i]!.end > t) return cues[i]!;
  }
  return null;
}

export class CaptionFileError extends Error {}

/** Reads an uploaded .srt / .vtt and returns WebVTT, or throws CaptionFileError. */
export async function readCaptionFile(file: File): Promise<{ vtt: string; cues: Cue[] }> {
  if (file.size > 512 * 1024) throw new CaptionFileError('That caption file is over 512 KB.');
  const text = await file.text();
  const isVtt = text.replace(/^\uFEFF/, '').startsWith('WEBVTT');
  const vtt = isVtt ? text : srtToVtt(text);
  const cues = parseVtt(vtt);
  if (cues.length === 0) {
    throw new CaptionFileError('That file has no captions we can read. Use a .srt or .vtt file.');
  }
  // Re-serialise even a .vtt: one clean shape, no STYLE blocks or markup reaching the server.
  return { vtt: cuesToVtt(cues), cues };
}
